import { Sparkle } from '@phosphor-icons/react';
import { Button, Icon, Sheet, Surface, Text } from '@shh/ui';
import { useState, type ReactNode } from 'react';
import { APP_VERSION, releases, shortVersion } from '../lib/releases';
import { todayText } from '../i18n/today';

const SEEN_KEY = 'shh.seenVersion';

/** **fett** and _(Hinweis)_ – the only formatting the release notes use. */
function inline(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*|_[^_]+_)/g).map((part, i) =>
    part.startsWith('**') ? (
      <strong key={i}>{part.slice(2, -2)}</strong>
    ) : part.startsWith('_') ? (
      <span key={i} className="text-text-secondary">
        {part.slice(1, -1)}
      </span>
    ) : (
      part
    ),
  );
}

/** All releases, newest first – the texts of apps/web/src/whats-new.md. */
function ReleaseNotes() {
  return (
    <div className="flex flex-col gap-section">
      {releases.map((r) => (
        <section
          key={r.version}
          aria-label={`Version ${r.version}`}
          className="flex flex-col gap-gap-sm"
        >
          <h3 className="font-display text-lg font-black">
            Version {r.version}
            {r.date && (
              <span className="text-sm font-semibold text-text-secondary"> · {r.date}</span>
            )}
          </h3>
          {r.intro.map((p) => (
            <Text key={p} size="sm" tone="secondary">
              {inline(p)}
            </Text>
          ))}
          <ul className="flex list-disc flex-col gap-gap-xs pl-inset-sm">
            {r.items.map((item) => (
              <li key={item}>{inline(item)}</li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

export function WhatsNewSheet({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  return (
    <Sheet isOpen={isOpen} onOpenChange={(open) => !open && onClose()} title={todayText.whatsNew}>
      <ReleaseNotes />
    </Sheet>
  );
}

function readSeen(): string | null {
  try {
    return localStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
}
function writeSeen() {
  try {
    localStorage.setItem(SEEN_KEY, APP_VERSION);
  } catch {
    // private mode: the hint simply shows again next time
  }
}

/**
 * After an update, once per device: "Neu in Version 1.2". A first visit stays quiet (everything
 * is new then) and only remembers the version.
 */
export function UpdateHint() {
  const [seen] = useState(readSeen);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const current = shortVersion(APP_VERSION);
  if (seen === null) {
    writeSeen();
    return null;
  }
  if (hidden || shortVersion(seen) === current) return null;
  const done = () => {
    writeSeen();
    setHidden(true);
  };
  return (
    <>
      <Surface tone="sunken" className="flex flex-wrap items-center gap-gap-sm" role="status">
        <Icon icon={Sparkle} size="md" weight="fill" className="text-accent-tertiary" />
        <Text weight="semibold" className="flex-1">
          Neu in Version {current}
        </Text>
        <Button variant="secondary" onPress={() => setOpen(true)}>
          {todayText.look}
        </Button>
        <Button variant="quiet" onPress={done}>
          {todayText.later}
        </Button>
      </Surface>
      <WhatsNewSheet
        isOpen={open}
        onClose={() => {
          setOpen(false);
          done();
        }}
      />
    </>
  );
}
